// frontend/src/pages/AuthPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { PREMIUM_PRICE_LABEL } from '../config/pricing';
import {
  Heart,
  Shield,
  Users,
  ArrowRight,
  Stethoscope,
  UserCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Sparkles,
  Brain,
  Activity,
  Zap,
  Star,
  CheckCircle,
  MessageCircle,
  Award,
  Lock,
  Clock,
  Infinity,
  ShieldCheck,
  Mail,
  Globe,
  KeyRound,
  Ban,
  Pill,
  FlaskConical,
  HeartPulse,
  Bell,
  MapPin,
  BookOpen,
  Home,
  ClipboardList,
  History,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useLocation, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, sendEmailVerification, applyActionCode } from 'firebase/auth';

import SEO from '../components/SEO';

// 🆕 Import du logo
const LOGO_URL = '/RemaH.png';
// Monogramme R plein cadre (fond blanc) — sert de tuile de marque dans le hero
const LOGO_MARK = '/logoplein.png';

/* ─────────────────────────────────────────────────────────────────────────────
   ÉLÉMENTS PUREMENT PRÉSENTATIONNELS (aucune logique)
   ───────────────────────────────────────────────────────────────────────────── */

// Particules d'ambiance : positions déterministes (calculées une seule fois,
// stables entre re-renders — pas de Math.random() dans le rendu).
const PARTICLES = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 67 + 11) % 100}%`,
  top: `${(i * 37 + 13) % 100}%`,
  delay: `${(i % 7) * 0.9}s`,
  duration: `${6 + (i % 5) * 2}s`,
}));

/* eslint-disable react/prop-types -- petits composants présentationnels internes à la page */

// Cadre téléphone premium pour mettre en scène les screenshots de l'app.
const PhoneMockup = ({ src, alt, width, height, eager = false, className = '' }) => (
  <div className={`relative ${className}`}>
    {/* Halo émeraude */}
    <div
      className="absolute -inset-6 bg-gradient-to-br from-emerald-500/25 via-teal-500/15 to-transparent rounded-[3rem] blur-2xl"
      aria-hidden="true"
    ></div>
    {/* Châssis */}
    <div className="relative rounded-[2.6rem] bg-slate-800/90 border border-white/10 p-2 shadow-2xl shadow-black/50 ring-1 ring-white/5">
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? 'eager' : 'lazy'}
        className="w-full h-auto rounded-[2.1rem] block"
      />
    </div>
  </div>
);

// Pastille d'intro de section
const SectionBadge = ({ icon: Icon, children, tone = 'emerald' }) => {
  const tones = {
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300',
    amber: 'bg-amber-500/10 border-amber-500/20 text-amber-300',
  };
  return (
    <div
      className={`inline-flex items-center gap-2 px-4 py-2 border rounded-full mb-6 ${tones[tone]}`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium tracking-wide">{children}</span>
    </div>
  );
};

// Puce de réassurance (hero)
const TrustChip = ({ icon: Icon, children }) => (
  <div className="inline-flex items-center gap-2 px-3.5 py-2 bg-white/5 border border-white/10 rounded-full backdrop-blur-sm">
    <Icon className="w-3.5 h-3.5 text-emerald-400" />
    <span className="text-[min(0.875rem,3.6vw)] text-white/70 font-medium">{children}</span>
  </div>
);

// Ligne de fonctionnalité éditoriale (texte + mockup en alternance)
const FeatureRow = ({
  badgeIcon,
  badgeText,
  title,
  description,
  bullets,
  media,
  reversed = false,
}) => (
  <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
    <div className={`min-w-0 ${reversed ? 'md:order-2' : ''}`}>
      <SectionBadge icon={badgeIcon}>{badgeText}</SectionBadge>
      <h3 className="text-[min(1.5rem,6.8vw)] sm:text-3xl md:text-4xl font-heading font-bold text-white tracking-tight mb-5 leading-tight">
        {title}
      </h3>
      <p className="text-lg text-white/70 leading-relaxed mb-7">{description}</p>
      <ul className="space-y-3">
        {bullets.map((item, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <span className="text-white/80">{item}</span>
          </li>
        ))}
      </ul>
    </div>
    <div className={`min-w-0 flex justify-center ${reversed ? 'md:order-1' : ''}`}>{media}</div>
  </div>
);

// Carte de la section Confiance & sécurité (faits vérifiés uniquement)
const TrustCard = ({ icon: Icon, title, children }) => (
  <div className="group p-6 bg-white/[0.04] border border-white/10 rounded-2xl hover:bg-white/[0.07] hover:border-emerald-500/30 transition-all duration-300">
    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:bg-emerald-500/15 transition-colors">
      <Icon className="w-6 h-6 text-emerald-400" />
    </div>
    <h4 className="text-white font-semibold mb-2">{title}</h4>
    <p className="text-sm text-white/60 leading-relaxed">{children}</p>
  </div>
);

/* eslint-enable react/prop-types */

/* ─────────────────────────────────────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────────────────────────────────────── */

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register, user, role, loading } = useAuth();
  const [formLoading, setFormLoading] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  // Login state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);

  // Register state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    birth_year: new Date().getFullYear() - 30,
    profile_type: '',
    subscription_type: 'free',
  });

  const getFirebaseErrorMessage = (errorCode) => {
    const errors = {
      'auth/user-not-found': 'Aucun compte associé à cet email',
      'auth/wrong-password': 'Mot de passe incorrect',
      'auth/invalid-email': "Format d'email invalide",
      'auth/user-disabled': 'Ce compte a été désactivé',
      'auth/too-many-requests': 'Trop de tentatives. Réessayez plus tard',
      'auth/invalid-credential': 'Email ou mot de passe incorrect',
      'auth/network-request-failed': 'Erreur de connexion réseau',
    };
    return errors[errorCode] || 'Erreur de connexion';
  };

  // 🎯 REDIRECTION RESET PASSWORD UNIQUEMENT
  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');
    if (mode === 'resetPassword' && oobCode) {
      navigate(`/reset-password?oobCode=${oobCode}`);
    }
  }, [searchParams, navigate]);

  // Parallax effect (desktop uniquement : évite les re-renders coûteux au scroll sur mobile)
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth < 1024) return;
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleEmailVerification = async (oobCode) => {
    setVerifying(true);
    try {
      await applyActionCode(auth, oobCode);

      const currentUser = auth.currentUser;
      if (currentUser) {
        await currentUser.reload();

        await currentUser.getIdToken(true);
      }

      setEmailVerified(true);
      toast.success('Email vérifié avec succès ! 🎉 Vous pouvez maintenant vous connecter.');
      window.history.replaceState({}, document.title, '/auth');
    } catch (error) {
      console.error('❌ Erreur vérification email - code:', error.code, 'message:', error.message);
      if (error.code === 'auth/invalid-action-code') {
        toast.error('Lien de vérification invalide ou expiré. Demandez un nouvel email.');
      } else if (error.code === 'auth/expired-action-code') {
        toast.error('Lien de vérification expiré. Demandez un nouvel email.');
      } else {
        toast.error('Erreur lors de la vérification. Réessayez.');
      }
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');

    if ((mode === 'verifyEmail' || mode === 'action') && oobCode) {
      setShowEmailVerification(false);
      handleEmailVerification(oobCode);
    }
  }, [searchParams]);

  useEffect(() => {
    const mode = searchParams.get('mode');

    if (loading) return;
    if (location.pathname !== '/' && location.pathname !== '/auth') return;

    if (mode === 'verifyEmail' || mode === 'action') return;

    if (user && role === 'admin') {
      navigate('/admin/dashboard', { replace: true });
    } else if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, role, loading, navigate, location.pathname, searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Connexion réussie !');
      navigate('/dashboard');
    } catch (error) {
      console.error('Erreur login:', error);

      if (error.isEmailNotVerified || error.message === 'EMAIL_NOT_VERIFIED') {
        setUserEmail(loginData.email);
        setShowEmailVerification(true);
        toast.error('Veuillez vérifier votre email avant de vous connecter');
      } else if (error.code) {
        toast.error(getFirebaseErrorMessage(error.code));
      } else {
        toast.error(error.message || 'Erreur de connexion');
      }
    } finally {
      setFormLoading(false);
    }
  };
  const validatePassword = (password) => {
    if (password.length < 8) return 'Le mot de passe doit contenir au moins 8 caractères';
    if (!/[A-Z]/.test(password)) return 'Le mot de passe doit contenir au moins une majuscule';
    if (!/[a-z]/.test(password)) return 'Le mot de passe doit contenir au moins une minuscule';
    if (!/[0-9]/.test(password)) return 'Le mot de passe doit contenir au moins un chiffre';
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password))
      return 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*...)';
    return null;
  };

  const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) score++;
    if (score <= 2) return { score, label: 'Faible', color: 'bg-red-500' };
    if (score <= 4) return { score, label: 'Moyen', color: 'bg-yellow-500' };
    return { score, label: 'Fort', color: 'bg-green-500' };
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!registerData.profile_type) {
      toast.error('Veuillez sélectionner votre profil professionnel');
      return;
    }

    const passwordError = validatePassword(registerData.password);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    if (registerData.password !== registerData.passwordConfirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setFormLoading(true);
    try {
      await register(
        registerData.email,
        registerData.password,
        registerData.first_name,
        registerData.last_name,
        registerData.birth_year,
        registerData.profile_type,
        registerData.subscription_type
      );
      setUserEmail(registerData.email);
      setShowEmailVerification(true);
      toast.success('Inscription réussie ! Vérifiez votre email (pensez à vos spams) 📧');
    } catch (error) {
      console.error('Erreur inscription:', error);
      if (error.code) {
        toast.error(getFirebaseErrorMessage(error.code));
      } else {
        toast.error(error.message || "Erreur lors de l'inscription");
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setFormLoading(true);
    try {
      const tempCredential = await signInWithEmailAndPassword(
        auth,
        userEmail,
        loginData.password || registerData.password
      );
      await sendEmailVerification(tempCredential.user, {
        url: `${window.location.origin}/auth`,
        handleCodeInApp: false,
      });
      await auth.signOut();
      toast.success('Email de vérification renvoyé ! Vérifiez votre boîte de réception (et vos spams) 📧');
    } catch (error) {
      console.error('Erreur renvoi email:', error);
      toast.error("Erreur lors du renvoi de l'email");
    } finally {
      setFormLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center relative overflow-hidden">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl animate-pulse pointer-events-none"
          aria-hidden="true"
        ></div>
        <div className="text-center relative z-10">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/25 animate-pulse">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <p className="text-white text-lg">Vérification de votre email...</p>
        </div>
      </div>
    );
  }

  if (emailVerified) {
    return (
      <div className="min-h-screen bg-slate-950 relative overflow-hidden flex items-center justify-center px-4">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        <Card className="relative z-10 p-8 bg-white/[0.06] backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/25">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-white mb-2">Email vérifié !</h2>
            <p className="text-white/60 text-sm mb-6">
              Votre compte est maintenant actif. Vous pouvez vous connecter.
            </p>
            <Button
              onClick={() => setEmailVerified(false)}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/20 py-6 text-lg rounded-xl transition-all"
            >
              Se connecter
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (showEmailVerification) {
    return (
      <div className="min-h-screen bg-slate-950 relative overflow-hidden flex items-center justify-center px-4">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        <Card className="relative z-10 p-8 bg-white/[0.06] backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/25">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-white mb-2">Vérifiez votre email</h2>
            <p className="text-white/60 text-sm mb-6">
              Un email de vérification a été envoyé à{' '}
              <span className="text-emerald-400">{userEmail}</span>
            </p>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
              <p className="text-white/80 text-sm mb-2">📬 Vérifiez votre boîte de réception</p>
              <p className="text-white/50 text-xs">
                Cliquez sur le lien dans l'email pour activer votre compte, puis revenez vous
                connecter.
              </p>
              <p className="text-white/80 text-sm mt-3 font-medium">
                📁 Pensez à regarder dans vos courriers indésirables (spam) : c'est souvent
                là que cet email arrive.
              </p>
            </div>
            <Button
              onClick={handleResendEmail}
              disabled={formLoading}
              className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 mb-4"
            >
              {formLoading ? 'Envoi...' : "Renvoyer l'email"}
            </Button>
            <Button
              onClick={() => setShowEmailVerification(false)}
              variant="ghost"
              className="w-full text-white/60 hover:text-white hover:bg-white/5"
            >
              Retour à la connexion
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="landing-root min-h-screen bg-slate-950 relative overflow-hidden">
      <SEO
        title="Connexion"
        description="Connectez-vous à Remadia pour accéder à votre assistant santé IA."
        path="/"
        responsive
      />
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        {/* Orbes émeraude avec parallaxe */}
        <div
          className="rma-orb absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-emerald-500/20 to-teal-500/10 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        ></div>
        <div
          className="rma-orb absolute top-1/4 right-0 w-[600px] h-[600px] bg-gradient-to-br from-teal-500/10 to-emerald-500/5 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * -0.15}px)`, animationDelay: '2s' }}
        ></div>
        <div
          className="rma-orb absolute bottom-0 left-1/3 w-[500px] h-[500px] bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * 0.2}px)`, animationDelay: '1s' }}
        ></div>

        {/* Particules flottantes (positions stables) */}
        {PARTICLES.map((p, i) => (
          <div
            key={i}
            className="rma-float absolute hidden md:block w-1.5 h-1.5 bg-emerald-200/20 rounded-full"
            style={{
              left: p.left,
              top: p.top,
              animationDelay: p.delay,
              animationDuration: p.duration,
            }}
          ></div>
        ))}

        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
      </div>

      <div className="relative z-10">
        {/* ── NAVBAR ─────────────────────────────────────────────────────── */}
        <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-3 sm:py-4 bg-slate-950/70 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Wordmark texte seul : le logo reste le moment fort unique du hero,
                la navbar porte le nom en clair (aucun doublon avec le hero). */}
            <div className="flex items-center">
              <span className="text-[min(1.25rem,5vw)] font-heading font-bold tracking-wide text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] whitespace-nowrap">
                Remadia <span className="text-emerald-400">Health</span>
              </span>
            </div>
            <Button
              onClick={() =>
                document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white px-4 sm:px-6 py-2 rounded-xl text-xs sm:text-sm font-semibold shadow-[0_0_20px_rgba(16,185,129,0.25)] focus-visible:ring-emerald-400"
            >
              <span className="hidden sm:inline">Connexion / Inscription</span>
              <span className="sm:hidden">Connexion</span>
            </Button>
          </div>
        </nav>

        {/* ── HERO ───────────────────────────────────────────────────────── */}
        <section className="relative min-h-screen flex items-center px-4 sm:px-6 lg:px-8 pt-28 pb-10 lg:pb-24">
          <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
            {/* Texte */}
            <div className="text-center lg:text-left min-w-0">
              {/* Eyebrow : catégorie produit */}
              <div
                className="rma-fade-up inline-flex items-center gap-2 mb-5"
                style={{ animationDelay: '0.05s' }}
              >
                <Sparkles className="w-4 h-4 text-emerald-400" />
                <span className="text-[min(0.875rem,3.4vw)] text-emerald-300 font-semibold tracking-[0.18em] uppercase">
                  L’IA santé française
                </span>
              </div>

              {/* Lockup de marque : logo + wordmark — élément DOMINANT du hero */}
              <div
                className="rma-fade-up flex items-center justify-center lg:justify-start gap-4 sm:gap-5 mb-6"
                style={{ animationDelay: '0.12s' }}
              >
                {/* Monogramme plein cadre en tuile arrondie : contraste maximal
                    du logo (le bleu marine ressort au lieu de se fondre dans le slate) */}
                <img
                  src={LOGO_MARK}
                  alt="Logo Remadia"
                  className="flex-shrink-0 w-[min(4.5rem,17vw)] h-[min(4.5rem,17vw)] sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-[1.4rem] object-cover bg-white shadow-[0_10px_35px_-8px_rgba(0,0,0,0.5)] ring-1 ring-white/70"
                />
                <span className="min-w-0 font-heading font-extrabold tracking-tight leading-none text-[min(2.75rem,11vw)] sm:text-6xl lg:text-7xl text-transparent bg-clip-text bg-gradient-to-br from-white via-white to-emerald-200 drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]">
                  Remadia
                </span>
              </div>

              {/* Accroche — forte mais un cran sous le wordmark */}
              <h1
                className="rma-fade-up text-[min(1.5rem,6.5vw)] md:text-3xl lg:text-4xl font-heading font-bold tracking-tight text-white/90 mb-6 leading-snug"
                style={{ animationDelay: '0.2s' }}
              >
                Transformez l’anxiété en{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-emerald-400 to-teal-400">
                  sérénité
                </span>
              </h1>

              <p
                className="rma-fade-up text-[min(1.125rem,4.8vw)] md:text-xl text-white/70 max-w-xl mx-auto lg:mx-0 leading-relaxed font-light mb-8"
                style={{ animationDelay: '0.25s' }}
              >
                Des réponses claires à vos questions de santé, vos analyses expliquées simplement,
                vos médicaments décryptés. Remadia vous aide à comprendre —{' '}
                <span className="text-white/90 font-normal">
                  votre médecin reste votre référence.
                </span>
              </p>

              {/* Puces de réassurance — faits vérifiés uniquement */}
              <div
                className="rma-fade-up flex flex-wrap justify-center lg:justify-start gap-2.5 mb-10"
                style={{ animationDelay: '0.35s' }}
              >
                <TrustChip icon={Lock}>Chiffré AES-256-GCM</TrustChip>
                <TrustChip icon={Globe}>Hébergé en Europe</TrustChip>
                <TrustChip icon={ShieldCheck}>Conforme RGPD</TrustChip>
                <TrustChip icon={Clock}>Disponible 24/7</TrustChip>
              </div>

              {/* CTA */}
              <div
                className="rma-fade-up flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center"
                style={{ animationDelay: '0.45s' }}
              >
                <Button
                  onClick={() =>
                    document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' })
                  }
                  size="lg"
                  className="group relative w-full sm:w-auto max-w-full whitespace-normal bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white text-[min(1.125rem,4.5vw)] font-medium px-8 py-6 shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:shadow-[0_0_40px_rgba(16,185,129,0.45)] transition-all transform hover:-translate-y-0.5 border-0 rounded-2xl overflow-hidden focus-visible:ring-emerald-400"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out"></span>
                  <span className="relative flex items-center justify-center gap-2">
                    Commencer gratuitement
                    <Sparkles className="w-5 h-5" />
                  </span>
                </Button>

                <Button
                  onClick={() =>
                    document
                      .getElementById('pricing-section')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                  size="lg"
                  variant="outline"
                  className="group w-full sm:w-auto max-w-full whitespace-normal bg-white/5 backdrop-blur-md border-2 border-white/15 hover:border-emerald-400/40 hover:bg-white/10 text-white hover:text-white text-[min(1.125rem,4.5vw)] font-medium px-8 py-6 rounded-2xl transition-all transform hover:-translate-y-0.5"
                >
                  Voir les tarifs
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>

            {/* Mockup héros : l'app réelle */}
            <div
              className="rma-fade-up relative flex justify-center lg:justify-end"
              style={{ animationDelay: '0.3s' }}
            >
              <div className="relative w-[260px] sm:w-[300px] lg:w-[330px]">
                <PhoneMockup
                  src="/Img-Auth/Dash.jpeg"
                  alt="Application Remadia — tableau de bord avec l'assistant IA santé"
                  width={1041}
                  height={2048}
                  eager
                />

                {/* Chips flottantes de mise en scène */}
                <div className="hidden lg:flex absolute -left-32 top-16 items-center gap-2.5 px-4 py-3 bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl shadow-xl">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold leading-tight">Assistant IA</p>
                    <p className="text-white/50 text-xs">Réponses claires</p>
                  </div>
                </div>

                <div className="hidden lg:flex absolute -right-24 bottom-24 items-center gap-2.5 px-4 py-3 bg-slate-900/80 backdrop-blur-xl border border-amber-500/20 rounded-2xl shadow-xl">
                  <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold leading-tight">Premium</p>
                    <p className="text-white/50 text-xs">Recherches illimitées</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 animate-bounce opacity-60 hover:opacity-100 transition-opacity"
            aria-hidden="true"
          >
            <div className="w-7 h-11 border-2 border-white/30 rounded-full flex justify-center pt-2">
              <div className="w-1 h-2.5 bg-emerald-300/80 rounded-full animate-pulse"></div>
            </div>
          </div>
        </section>

        {/* ── FEATURES ───────────────────────────────────────────────────── */}
        <section className="py-16 md:py-32 bg-gradient-to-b from-transparent via-slate-900/60 to-transparent backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-24">
              <SectionBadge icon={Star}>Fonctionnalités</SectionBadge>
              <h2 className="text-[min(1.875rem,8vw)] md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6">
                <span className="bg-gradient-to-r from-emerald-300 to-teal-400 bg-clip-text text-transparent">
                  Une plateforme pensée pour vous rassurer
                </span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                Chaque outil transforme des informations médicales complexes en explications
                claires et apaisantes.
              </p>
            </div>

            <div className="space-y-16 md:space-y-32">
              {/* 1. Chat IA */}
              <div data-testid="feature-card-professionals">
                <FeatureRow
                  badgeIcon={MessageCircle}
                  badgeText="Assistant santé"
                  title="Posez vos questions, recevez des réponses claires"
                  description="Décrivez ce que vous ressentez en langage courant. L'assistant vous répond simplement, sans jargon, et vous aide à y voir plus clair."
                  bullets={[
                    'Des explications claires, adaptées à votre profil',
                    "Un accompagnement attentif, qui sait vous orienter vers un professionnel quand c'est nécessaire",
                    'Disponible à toute heure, dans un langage simple et rassurant',
                  ]}
                  media={
                    <PhoneMockup
                      src="/Img-Auth/chatIA.jpeg"
                      alt="Chat médical Remadia — réponses personnalisées avec rappel de consulter un professionnel de santé"
                      width={1046}
                      height={2048}
                      className="w-[240px] sm:w-[280px]"
                    />
                  }
                />
              </div>

              {/* 2. Analyses */}
              <FeatureRow
                reversed
                badgeIcon={FlaskConical}
                badgeText="Analyses vulgarisées"
                title="Vos analyses de sang, enfin compréhensibles"
                description="Photographiez vos résultats : Remadia les traduit en langage clair — ce que mesure chaque marqueur, où vous vous situez, et ce qui mérite d'être évoqué avec votre médecin."
                bullets={[
                  'Photo → résultats expliqués simplement',
                  'Repères clairs : dans / hors de l\'intervalle indiqué',
                  'Chaque marqueur expliqué, sans alarmisme',
                ]}
                media={
                  <PhoneMockup
                    src="/Img-Auth/Analyse.jpeg"
                    alt="Analyse de sang expliquée dans Remadia — sérologie détaillée en langage simple"
                    width={1050}
                    height={2048}
                    className="w-[240px] sm:w-[280px]"
                  />
                }
              />

              {/* 3. Médicaments */}
              <FeatureRow
                badgeIcon={Pill}
                badgeText="Base médicaments"
                title="Vos médicaments, décryptés"
                description="Indications, posologie, effets, interactions, grossesse et allaitement : l'essentiel sur vos traitements, à partir de la base officielle française du médicament."
                bullets={[
                  'Fiches fondées sur la base officielle (BDPM) — aucune information inventée',
                  'Interactions et précautions signalées clairement',
                  'Un chat dédié à chaque médicament pour vos questions précises',
                ]}
                media={
                  <PhoneMockup
                    src="/Img-Auth/Medic.jpeg"
                    alt="Fiche médicament Doliprane dans Remadia — indications et interactions"
                    width={1045}
                    height={2048}
                    className="w-[240px] sm:w-[280px]"
                  />
                }
              />

              {/* 4. Carnet & suivi */}
              <FeatureRow
                reversed
                badgeIcon={HeartPulse}
                badgeText="Carnet de santé"
                title="Suivez vos constantes, comprenez vos tendances"
                description="Tension, glycémie, poids, température, saturation, fréquence cardiaque : enregistrez vos mesures, visualisez leur évolution, et gardez une vue claire de votre santé."
                bullets={[
                  'Vos mesures suivies dans le temps, avec leur tendance',
                  "Un profil santé qui affine les réponses de l'assistant",
                  'Historique clair, données chiffrées',
                ]}
                media={
                  <div className="flex items-center justify-center">
                    <PhoneMockup
                      src="/Img-Auth/profilSante.jpeg"
                      alt="Suivi des constantes dans Remadia — tension, poids, température, saturation"
                      width={1041}
                      height={2048}
                      className="w-[150px] sm:w-[230px] -rotate-3"
                    />
                    <PhoneMockup
                      src="/Img-Auth/carnetSante.jpeg"
                      alt="Profil santé Remadia — informations générales du carnet de santé"
                      width={1032}
                      height={2048}
                      className="w-[150px] sm:w-[230px] rotate-3 -ml-7 sm:-ml-12 mt-10 sm:mt-14"
                    />
                  </div>
                }
              />

              {/* 5. Et aussi — écosystème (remplace « Communauté & ressources ») */}
              <div>
                <div className="text-center mb-14 md:mb-16">
                  <SectionBadge icon={Sparkles}>Et aussi</SectionBadge>
                  <h3 className="text-[min(1.75rem,7.5vw)] md:text-4xl lg:text-5xl font-heading font-bold tracking-tight mb-5">
                    <span className="bg-gradient-to-r from-emerald-300 to-teal-400 bg-clip-text text-transparent">
                      Tout un écosystème pour prendre soin de vous
                    </span>
                  </h3>
                  <p className="text-lg text-white/70 max-w-3xl mx-auto leading-relaxed">
                    Au-delà de l'assistant, Remadia réunit des outils et une communauté pour vous
                    accompagner au quotidien.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {[
                    {
                      icon: Users,
                      title: 'Forum communautaire',
                      text: "Échangez en toute confidentialité, sous pseudonyme, avec d'autres utilisateurs.",
                      testId: 'feature-card-forumpage',
                    },
                    {
                      icon: Bell,
                      title: 'Alertes santé',
                      text: "Qualité de l'air, pollens et épidémies saisonnières, région par région.",
                    },
                    {
                      icon: MapPin,
                      title: 'Urgences & pharmacies',
                      text: 'Numéros essentiels et pharmacies proches de chez vous.',
                    },
                    {
                      icon: BookOpen,
                      title: 'Articles santé',
                      text: "Une bibliothèque d'articles rédigés par l'équipe, en accès libre.",
                    },
                    {
                      icon: Home,
                      title: 'Aide au quotidien',
                      text: "Un assistant dédié à l'organisation de la maison et à l'autonomie.",
                    },
                    {
                      icon: ClipboardList,
                      title: 'Évaluation des besoins',
                      text: 'Un questionnaire personnalisé selon votre âge et votre situation.',
                    },
                    {
                      icon: History,
                      title: 'Historique',
                      text: 'Toutes vos conversations conservées, chiffrées et faciles à retrouver.',
                    },
                  ].map((t, idx) => (
                    <div
                      key={idx}
                      {...(t.testId ? { 'data-testid': t.testId } : {})}
                      className="group p-5 bg-white/[0.04] border border-white/10 rounded-2xl hover:bg-white/[0.07] hover:border-emerald-500/30 transition-all duration-300"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/15 transition-colors">
                          <t.icon className="w-5 h-5 text-emerald-400" />
                        </div>
                        <h4 className="text-white font-semibold">{t.title}</h4>
                      </div>
                      <p className="text-sm text-white/60 leading-relaxed">{t.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CONFIANCE & SÉCURITÉ ───────────────────────────────────────── */}
        <section className="py-16 md:py-32 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 md:mb-20">
              <SectionBadge icon={ShieldCheck}>Confiance & sécurité</SectionBadge>
              <h2 className="text-[min(1.875rem,8vw)] md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6">
                <span className="bg-gradient-to-r from-emerald-300 to-teal-400 bg-clip-text text-transparent">
                  Vos données de santé, protégées
                </span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                Pas de promesses vagues — des engagements précis et vérifiables.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto">
              <TrustCard icon={Lock} title="Chiffrement AES-256-GCM">
                Vos conversations et données de santé sont chiffrées côté serveur avec
                l’algorithme AES-256-GCM.
              </TrustCard>
              <TrustCard icon={KeyRound} title="Une clé par utilisateur">
                Chaque compte dispose de sa propre clé de chiffrement : vos données sont isolées
                des autres utilisateurs.
              </TrustCard>
              <TrustCard icon={Globe} title="Hébergement en Europe">
                Vos données sont hébergées en Europe et traitées dans le respect du droit
                européen.
              </TrustCard>
              <TrustCard icon={ShieldCheck} title="Conforme RGPD">
                Droit d’accès et de suppression : compte supprimé sous 30 jours, données
                conservées au maximum 2 ans.
              </TrustCard>
              <TrustCard icon={Ban} title="Zéro publicité, zéro revente">
                Aucune publicité dans l’application. Vos données ne sont jamais revendues à des
                tiers.
              </TrustCard>
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ───────────────────────────────────────────────── */}
        <section className="py-16 md:py-32 bg-gradient-to-b from-transparent via-slate-900/50 to-transparent backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 md:mb-20">
              <SectionBadge icon={Zap}>Simple & efficace</SectionBadge>
              <h2 className="text-[min(1.875rem,8vw)] md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6">
                <span className="bg-gradient-to-r from-emerald-300 to-teal-400 bg-clip-text text-transparent">
                  Comment ça marche ?
                </span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                Trois étapes pour passer des questions anxieuses aux réponses posées.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-14 md:gap-20 items-center max-w-6xl mx-auto">
              {/* Étapes */}
              <div className="space-y-10">
                {[
                  {
                    number: '1',
                    icon: UserCircle,
                    title: 'Créez votre compte',
                    description:
                      'Inscription gratuite en moins d’une minute. Confirmez votre e-mail, et vous êtes prêt.',
                  },
                  {
                    number: '2',
                    icon: Brain,
                    title: 'Posez vos questions',
                    description:
                      'L’assistant analyse votre demande et vous répond instantanément, en langage clair et sans jargon.',
                  },
                  {
                    number: '3',
                    icon: Heart,
                    title: 'Avancez plus sereinement',
                    description:
                      'Comprenez mieux votre santé, suivez vos constantes, échangez avec la communauté — votre médecin reste votre référence.',
                  },
                ].map((step, idx) => (
                  <div key={idx} className="flex gap-5 group min-w-0">
                    <div className="flex flex-col items-center">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl blur-lg opacity-40 group-hover:opacity-70 transition-opacity"></div>
                        <div className="relative w-[min(3.5rem,13vw)] h-[min(3.5rem,13vw)] rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-xl transform group-hover:scale-105 transition-transform">
                          <span className="text-[min(1.5rem,6vw)] font-bold text-white">{step.number}</span>
                        </div>
                      </div>
                      {idx < 2 && (
                        <div className="w-px flex-1 mt-3 bg-gradient-to-b from-emerald-500/40 to-transparent"></div>
                      )}
                    </div>
                    <div className="pb-2 min-w-0 flex-1">
                      <div className="flex items-center gap-2.5 mb-2">
                        <step.icon className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        <h3 className="text-[min(1.25rem,5.5vw)] md:text-2xl font-heading font-bold text-white group-hover:text-emerald-300 transition-colors">
                          {step.title}
                        </h3>
                      </div>
                      <p className="text-white/70 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Vue d'ensemble des outils */}
              <div className="flex flex-col items-center">
                <PhoneMockup
                  src="/Img-Auth/function2.jpeg"
                  alt="Ma Santé dans Remadia — carnet de santé, analyse de sang, pharmacie et pilulier"
                  width={1049}
                  height={2048}
                  className="w-[240px] sm:w-[290px]"
                />
                <p className="mt-6 text-sm text-white/50 text-center max-w-[min(20rem,86vw)]">
                  Tous vos outils santé, réunis dans une seule application.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── PRICING ────────────────────────────────────────────────────── */}
        <section id="pricing-section" className="py-16 md:py-32 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 md:mb-20">
              <SectionBadge icon={Sparkles} tone="amber">
                Tarification transparente
              </SectionBadge>
              <h2 className="text-[min(1.875rem,8vw)] md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6">
                <span className="bg-gradient-to-r from-emerald-300 to-teal-400 bg-clip-text text-transparent">
                  Choisissez votre plan
                </span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                Des tarifs adaptés à tous, sans engagement. Changez de formule à tout moment.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* FREE PLAN */}
              <Card className="group relative p-8 md:p-10 bg-white/[0.04] border border-white/10 backdrop-blur-md hover:border-emerald-500/30 hover:bg-white/[0.06] rounded-3xl transition-all duration-300 overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-3xl font-heading font-bold text-white">Gratuit</h3>
                  </div>
                  <div className="mb-8">
                    <div className="text-[min(3.75rem,14vw)] font-heading font-bold text-white mb-2">0€</div>
                    <p className="text-white/60">Pour découvrir la plateforme</p>
                  </div>
                  <ul className="space-y-4 mb-10">
                    {[
                      { icon: Zap, text: '3 questions par jour', highlight: true },
                      { icon: MessageCircle, text: 'Accès au forum communautaire' },
                      { icon: Activity, text: 'Base de données médicaments' },
                      { icon: CheckCircle, text: 'Articles santé validés' },
                      { icon: Lock, text: 'Données sécurisées RGPD' },
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 group/item">
                        <div
                          className={`w-6 h-6 rounded-lg ${item.highlight ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : 'bg-white/10'} flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform`}
                        >
                          <item.icon
                            className={`w-3.5 h-3.5 ${item.highlight ? 'text-white' : 'text-emerald-400'}`}
                          />
                        </div>
                        <span className="text-white/80">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() =>
                      document
                        .getElementById('auth-section')
                        ?.scrollIntoView({ behavior: 'smooth' })
                    }
                    className="w-full max-w-full whitespace-normal bg-white/10 hover:bg-white/15 text-white border border-white/20 hover:border-emerald-400/40 py-6 text-[min(1.125rem,4.5vw)] rounded-xl transition-all"
                  >
                    Commencer gratuitement
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </Card>

              {/* PREMIUM PLAN */}
              <Card className="group relative p-8 md:p-10 bg-gradient-to-b from-slate-800/90 to-slate-900/90 border-2 border-amber-400/50 hover:border-amber-400/80 backdrop-blur-md rounded-3xl transition-all duration-300 shadow-2xl shadow-amber-500/10 hover:shadow-amber-500/20 overflow-hidden">
                {/* Halo ambre discret */}
                <div
                  className="absolute -top-24 -right-24 w-64 h-64 bg-amber-400/10 rounded-full blur-3xl pointer-events-none"
                  aria-hidden="true"
                ></div>

                {/* Popular badge */}
                <div className="absolute top-0 right-0 px-5 py-2 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-bold text-xs tracking-wide rounded-bl-2xl shadow-lg flex items-center gap-1.5">
                  <Star className="w-3.5 h-3.5" />
                  POPULAIRE
                </div>

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="text-3xl font-heading font-bold text-white">Premium</h3>
                  </div>
                  <div className="mb-8">
                    <div className="text-[min(3.75rem,14vw)] font-heading font-bold text-white mb-2 flex items-baseline gap-2">
                      {PREMIUM_PRICE_LABEL}
                      <span className="text-2xl text-white/60 font-medium">/mois</span>
                    </div>
                    <p className="text-white/70">L'excellence sans limite</p>
                  </div>
                  <ul className="space-y-4 mb-10">
                    {[
                      {
                        icon: Infinity,
                        text: 'Recherches illimitées dans toutes les fonctionnalités',
                        highlight: true,
                      },
                      { icon: Zap, text: 'Réponses prioritaires (plus rapides)', highlight: true },
                      { icon: Clock, text: 'Historique complet des conversations' },
                      { icon: Shield, text: 'Support prioritaire par email' },
                      { icon: Star, text: 'Accès anticipé aux nouvelles fonctionnalités' },
                      { icon: Award, text: 'Badge Premium dans le forum' },
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 group/item">
                        <div
                          className={`w-6 h-6 rounded-lg ${item.highlight ? 'bg-amber-400/25 border border-amber-400/30' : 'bg-white/10'} flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform`}
                        >
                          <item.icon
                            className={`w-3.5 h-3.5 ${item.highlight ? 'text-amber-300' : 'text-emerald-400'}`}
                          />
                        </div>
                        <span
                          className={`${item.highlight ? 'text-white font-semibold' : 'text-white/80'}`}
                        >
                          {item.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() =>
                      document
                        .getElementById('auth-section')
                        ?.scrollIntoView({ behavior: 'smooth' })
                    }
                    className="w-full max-w-full whitespace-normal bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-bold py-6 text-[min(1.125rem,4.5vw)] rounded-xl shadow-xl shadow-amber-500/20 hover:shadow-amber-500/30 transition-all"
                  >
                    Essayer Premium
                    <Sparkles className="ml-2 w-5 h-5" />
                  </Button>

                  {/* Money-back guarantee */}
                  <div className="mt-4 flex items-center justify-center gap-2 text-white/60 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Sans engagement, résiliez quand vous voulez
                  </div>
                </div>
              </Card>
            </div>

            {/* Trust indicators */}
            <div className="mt-16 text-center">
              <p className="text-white/50 text-sm mb-5">
                Rejoignez les premiers utilisateurs de Remadia Health
              </p>
              <div className="flex justify-center items-center gap-3 sm:gap-4 flex-wrap">
                {[
                  'Conforme RGPD',
                  'Chiffrement AES-256-GCM & SSL',
                  'OAuth 2.0',
                  'Stripe (DSS)',
                ].map((cert, idx) => (
                  <div
                    key={idx}
                    className="px-5 py-2.5 bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-lg"
                  >
                    <span className="text-white/70 text-sm font-medium">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── REMADIA PRO (à venir) ──────────────────────────────────────── */}
        <section className="py-10 md:py-20 relative">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-md p-6 sm:p-8 md:p-12">
              <div
                className="absolute -top-24 -right-24 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"
                aria-hidden="true"
              ></div>
              <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-8">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                      <Stethoscope className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-xs sm:text-sm text-emerald-200 font-medium">
                        Remadia Pro
                      </span>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-1 bg-amber-400/15 border border-amber-400/30 text-amber-300 text-[11px] font-bold uppercase tracking-wide rounded-full">
                      Bientôt disponible
                    </span>
                  </div>
                  <h2 className="text-[min(1.5rem,6.8vw)] md:text-3xl font-heading font-bold text-white tracking-tight mb-3">
                    Un espace dédié aux soignants
                  </h2>
                  <p className="text-white/70 leading-relaxed max-w-2xl">
                    Infirmiers et médecins : un espace professionnel avec des outils métier — chat
                    clinique d'aide à la réflexion, calculateurs de doses et de débits, scores
                    cliniques (Glasgow, Braden, SOFA…), protocoles de soins et base médicaments
                    professionnelle.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    className="inline-flex items-center gap-2 px-6 py-4 rounded-xl bg-white/5 border border-white/10 text-white/40 font-semibold cursor-not-allowed"
                  >
                    <Clock className="w-5 h-5" />
                    Bientôt disponible
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── AUTH SECTION ───────────────────────────────────────────────── */}
        <section id="auth-section" className="py-16 md:py-32 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-[min(1.875rem,8vw)] md:text-4xl font-heading font-bold text-white tracking-tight mb-3">
                  Rejoignez Remadia
                </h2>
                <p className="text-white/60">
                  Gratuit, sans engagement — vos données restent chiffrées et privées.
                </p>
              </div>

              {/* Glow effect */}
              <div
                className="absolute inset-x-0 max-w-md mx-auto h-96 bg-emerald-500/10 rounded-3xl blur-3xl pointer-events-none"
                aria-hidden="true"
              ></div>

              <Card className="relative p-8 bg-white/[0.06] backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-8 bg-white/5 p-1 rounded-xl h-auto">
                    <TabsTrigger
                      value="login"
                      data-testid="login-tab"
                      className="rounded-lg py-2 text-white/60 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white transition-all"
                    >
                      Connexion
                    </TabsTrigger>
                    <TabsTrigger
                      value="register"
                      data-testid="register-tab"
                      className="rounded-lg py-2 text-white/60 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white transition-all"

                      // DESACTIVATRION INSCRIPTION
                      //className="rounded-lg text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed pointer-events-none"title="Bientôt disponible"
                    >
                      Inscription
                    </TabsTrigger>
                  </TabsList>

                  {/* Login Form */}
                  <TabsContent value="login">
                    <form onSubmit={handleLogin} className="space-y-5">
                      <div>
                        <Label htmlFor="login-email" className="text-white mb-2 block">
                          Email
                        </Label>
                        <Input
                          id="login-email"
                          type="email"
                          data-testid="login-email-input"
                          placeholder="votre@email.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400 focus:ring-emerald-400/20 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="login-password" className="text-white mb-2 block">
                          Mot de passe
                        </Label>
                        <Input
                          id="login-password"
                          type="password"
                          data-testid="login-password-input"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400 focus:ring-emerald-400/20 rounded-xl"
                        />
                      </div>
                      <div>
                        {/* Mot de passe oublié */}
                        <button
                          type="button"
                          onClick={() => navigate('/forgot-password')}
                          className="mt-1 text-sm text-emerald-400 hover:text-emerald-300 transition-colors float-right"
                        >
                          Mot de passe oublié ?
                        </button>
                      </div>
                      <Button
                        type="submit"
                        data-testid="login-submit-button"
                        className="w-full max-w-full whitespace-normal bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 py-6 text-[min(1.125rem,4.5vw)] rounded-xl transition-all"
                        disabled={formLoading}
                      >
                        {formLoading ? 'Connexion...' : 'Se connecter'}
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                    </form>
                  </TabsContent>

                  {/* Register Form */}
                  <TabsContent value="register">
                    <form onSubmit={handleRegister} className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="first-name" className="text-white mb-2 block">
                            Prénom
                          </Label>
                          <Input
                            id="first-name"
                            data-testid="register-first-name-input"
                            placeholder="Jean"
                            value={registerData.first_name}
                            onChange={(e) =>
                              setRegisterData({ ...registerData, first_name: e.target.value })
                            }
                            required
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400 focus:ring-emerald-400/20 rounded-xl"
                          />
                        </div>
                        <div>
                          <Label htmlFor="last-name" className="text-white mb-2 block">
                            Nom
                          </Label>
                          <Input
                            id="last-name"
                            data-testid="register-last-name-input"
                            placeholder="Dupont"
                            value={registerData.last_name}
                            onChange={(e) =>
                              setRegisterData({ ...registerData, last_name: e.target.value })
                            }
                            required
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400 focus:ring-emerald-400/20 rounded-xl"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="register-email" className="text-white mb-2 block">
                          Email
                        </Label>
                        <Input
                          id="register-email"
                          type="email"
                          data-testid="register-email-input"
                          placeholder="votre@email.com"
                          value={registerData.email}
                          onChange={(e) =>
                            setRegisterData({ ...registerData, email: e.target.value })
                          }
                          required
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400 focus:ring-emerald-400/20 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="register-password" className="text-white mb-2 block">
                          Mot de passe
                        </Label>
                        <div className="relative">
                          <Input
                            id="register-password"
                            type={showPassword ? 'text' : 'password'}
                            data-testid="register-password-input"
                            placeholder="••••••••"
                            value={registerData.password}
                            onChange={(e) =>
                              setRegisterData({ ...registerData, password: e.target.value })
                            }
                            required
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400 focus:ring-emerald-400/20 rounded-xl pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                        {registerData.password &&
                          (() => {
                            const strength = getPasswordStrength(registerData.password);
                            const criteria = [
                              {
                                label: '8 caractères minimum',
                                met: registerData.password.length >= 8,
                              },
                              { label: '1 majuscule', met: /[A-Z]/.test(registerData.password) },
                              { label: '1 minuscule', met: /[a-z]/.test(registerData.password) },
                              { label: '1 chiffre', met: /[0-9]/.test(registerData.password) },
                              {
                                label: '1 caractère spécial',
                                met: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(registerData.password),
                              },
                            ];
                            return (
                              <div className="mt-2 space-y-2">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden flex gap-0.5">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                      <div
                                        key={i}
                                        className={`flex-1 h-full rounded-full transition-all duration-300 ${i <= strength.score ? strength.color : 'bg-white/10'}`}
                                      />
                                    ))}
                                  </div>
                                  <span
                                    className={`text-xs font-medium ${strength.score <= 2 ? 'text-red-400' : strength.score <= 4 ? 'text-yellow-400' : 'text-green-400'}`}
                                  >
                                    {strength.label}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                                  {criteria.map((c) => (
                                    <span
                                      key={c.label}
                                      className={`text-xs flex items-center gap-1 ${c.met ? 'text-green-400' : 'text-white/40'}`}
                                    >
                                      {c.met ? '✅' : '❌'} {c.label}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                      </div>
                      <div>
                        <Label
                          htmlFor="register-password-confirm"
                          className="text-white mb-2 block"
                        >
                          Confirmer le mot de passe
                        </Label>
                        <div className="relative">
                          <Input
                            id="register-password-confirm"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={registerData.passwordConfirm || ''}
                            onChange={(e) =>
                              setRegisterData({ ...registerData, passwordConfirm: e.target.value })
                            }
                            required
                            className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400 focus:ring-emerald-400/20 rounded-xl pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                          >
                            {showPassword ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="birth-year" className="text-white mb-2 block">
                          Année de naissance
                        </Label>
                        <Input
                          id="birth-year"
                          type="number"
                          data-testid="register-birth-year-input"
                          min="1920"
                          max={new Date().getFullYear()}
                          value={registerData.birth_year}
                          onChange={(e) =>
                            setRegisterData({
                              ...registerData,
                              birth_year: parseInt(e.target.value),
                            })
                          }
                          required
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400 focus:ring-emerald-400/20 rounded-xl"
                        />
                      </div>

                      <div>
                        <Label
                          htmlFor="profile-type"
                          className="flex items-center gap-2 text-white mb-2"
                        >
                          <span>Profil</span>
                          <span className="text-amber-400">*</span>
                        </Label>
                        <Select
                          value={registerData.profile_type}
                          onValueChange={(value) =>
                            setRegisterData({ ...registerData, profile_type: value })
                          }
                        >
                          <SelectTrigger
                            className={`bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-xl ${!registerData.profile_type ? 'border-amber-400/50' : ''}`}
                            data-testid="register-profile-type-select"
                          >
                            <SelectValue
                              placeholder="Sélectionnez votre profil"
                              className="text-white/60"
                            />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-white/20 [&_[data-highlighted]]:bg-white/20 [&_[data-highlighted]]:text-white">
                            <SelectItem
                              value="non_medical"
                              className="text-white hover:bg-white/10"
                            >
                              <div className="flex items-center gap-2">
                                <UserCircle className="w-4 h-4" />
                                <span>Patient / Grand public</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>

                        <div className="mt-3 flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-200">
                            <strong>Important :</strong> Ce choix est définitif et déterminera votre
                            expérience sur la plateforme.
                          </p>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="subscription" className="text-white mb-2 block">
                          Abonnement
                        </Label>

                        <p className="text-white/60 text-sm mt-2">
                          💡 Passez à Premium plus tard pour des recherches illimitées
                        </p>
                      </div>

                      <Button
                        type="submit"
                        data-testid="register-submit-button"
                        className="w-full max-w-full whitespace-normal bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 py-6 text-[min(1.125rem,4.5vw)] rounded-xl transition-all"
                        disabled={formLoading || !registerData.profile_type}
                      >
                        {formLoading ? 'Inscription...' : "S'inscrire"}
                        <Sparkles className="ml-2 w-5 h-5" />
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </Card>

              {/* Lien espace professionnel — désactivé (Remadia Pro à venir) */}
              <div className="relative z-10 mt-6 text-center">
                <p className="text-white/40 text-sm inline-flex items-center gap-1.5 flex-wrap justify-center">
                  <Stethoscope className="w-4 h-4 text-white/40" />
                  Vous êtes professionnel de santé ?{' '}
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    className="text-white/40 font-medium underline underline-offset-2 cursor-not-allowed"
                  >
                    Accéder à l'espace professionnel
                  </button>
                  <span className="inline-flex items-center px-2 py-0.5 bg-amber-400/15 border border-amber-400/30 text-amber-300 text-[10px] font-bold uppercase tracking-wide rounded-full">
                    Bientôt
                  </span>
                </p>
              </div>

              {/* Vandomia branding */}
              <div className="mt-8 text-center">
                <p className="text-xs text-white/30">
                  Remadia est une marque{' '}
                  <a
                    href="https://vandomia.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/50 hover:text-emerald-300/80 transition-colors"
                  >
                    Vandomia
                  </a>
                </p>
              </div>

              {/* 📱 Stores — Google Play disponible, App Store bientôt */}
              <div className="flex flex-col items-center gap-4 mt-8">
                <div className="flex flex-col items-center gap-1.5 text-center">
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
                    <Globe className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-xs sm:text-sm text-emerald-200 font-medium">
                      Disponible sur le web et sur mobile
                    </span>
                  </div>
                  <p className="text-sm text-white/50 font-medium">
                    Retrouvez Remadia partout — téléchargez aussi l’application mobile
                  </p>
                </div>
                <div className="flex gap-4">
                  {/* Google Play - DISPONIBLE */}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.remiremi.remadia&utm_source=emea_Med"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Télécharger Remadia sur Google Play"
                    className="relative group"
                  >
                    <div className="px-4 py-2 bg-black border border-gray-700 group-hover:border-emerald-500/50 rounded-xl flex items-center gap-2 transition-all group-hover:shadow-[0_0_20px_rgba(16,185,129,0.25)] group-hover:-translate-y-0.5">
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5Z"
                        />
                        <path fill="#34A853" d="M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12Z" />
                        <path
                          fill="#FBBC05"
                          d="M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81Z"
                        />
                        <path fill="#EA4335" d="M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                      </svg>
                      <div className="text-left">
                        <p className="text-[10px] text-gray-400 leading-none">Disponible sur</p>
                        <p className="text-sm font-semibold text-white leading-tight">
                          Google Play
                        </p>
                      </div>
                    </div>
                    <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold rounded-full shadow-lg">
                      Disponible
                    </span>
                  </a>

                  {/* App Store - Bientôt */}
                  <div className="relative group">
                    <div className="px-4 py-2 bg-black border border-gray-700 rounded-xl flex items-center gap-2 opacity-80 cursor-not-allowed hover:opacity-100 transition-opacity">
                      <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                      </svg>
                      <div className="text-left">
                        <p className="text-[10px] text-gray-400 leading-none">Télécharger sur</p>
                        <p className="text-sm font-semibold text-white leading-tight">App Store</p>
                      </div>
                    </div>
                    <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold rounded-full shadow-lg">
                      Bientôt
                    </span>
                  </div>
                </div>
              </div>

              {/* Mention d'information médicale — sobre et posée */}
              <div className="mt-10 pt-8 border-t border-white/10 text-center">
                <p className="text-xs text-white/50 leading-relaxed max-w-md mx-auto">
                  Remadia est un outil d’information et d’aide à la compréhension. Il ne remplace
                  pas l’avis d’un professionnel de santé. En cas d’urgence, composez le 15 (SAMU)
                  ou le 112.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes rma-float {
          0%,
          100% {
            transform: translateY(0px);
            opacity: 0.2;
          }
          50% {
            transform: translateY(-20px);
            opacity: 0.5;
          }
        }
        @keyframes rma-fade-up {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .rma-float {
          animation: rma-float linear infinite;
        }
        .rma-fade-up {
          animation: rma-fade-up 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        /* Filet anti-débordement : les gros titres cassent le mot plutôt que le layout */
        .landing-root h1,
        .landing-root h2,
        .landing-root h3 {
          overflow-wrap: break-word;
        }
        @media (prefers-reduced-motion: reduce) {
          .rma-float,
          .rma-fade-up {
            animation: none !important;
            opacity: 1 !important;
          }
          .rma-orb {
            transform: none !important;
            animation: none !important;
          }
          .landing-root .animate-pulse,
          .landing-root .animate-bounce {
            animation: none !important;
          }
        }
        /* Mobile : orbes statiques (le blur animé est trop coûteux en GPU) */
        @media (max-width: 1023px) {
          .rma-orb {
            transform: none !important;
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AuthPage;
